import * as React from 'react';

import { get as getUser } from 'js/lib/user';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { isCoachTutorGAEpic, useProductFeatures } from 'bundles/ai-course-coach/utils/productFeatureFlags';
import { captureMessageInSentry } from 'bundles/ai-course-coach/utils/sentryUtils';
import { IdTypes } from 'bundles/product-features';

type Props = {
  children: (props: { showEmbeddedCoach: boolean }) => JSX.Element | null;
  epicImpression?: boolean;
};

function useCoachPermissions({ epicImpression }: { epicImpression?: boolean } = {}) {
  const userId = getUser().id?.toString();
  const [showEmbeddedCoach, setShowEmbeddedCoach] = React.useState<boolean>(false);

  const { loading, error, features } = useProductFeatures({ idType: IdTypes.User, id: userId });
  const isCoachEnterpriseEnabled = features?.get?.('enterprise', 'enableAiCoach');
  const isCoachDegreesEnabled = features?.get?.('degrees', 'enableAiCoachForDegrees');
  const isCoachTutorGA = isCoachTutorGAEpic({ epicImpression });

  React.useEffect(() => {
    if (loading || error || !features) {
      if (error) {
        captureMessageInSentry({
          level: 'warning',
          error,
          source: 'useCoachPermissions',
          extra: {
            errorString: JSON.stringify(error, null, 2),
            externalId: getUser().external_id,
            isCoachEnterpriseEnabled,
            isCoachDegreesEnabled,
            isCoachTutorGA,
          },
        });
      }
      setShowEmbeddedCoach(false);
      return;
    }

    // always enabled for Enterprise, if it is not enterprise enabled then check the existing access group or holdout GA epic
    setShowEmbeddedCoach(isCoachDegreesEnabled || isCoachEnterpriseEnabled || isCoachTutorGA);
  }, [loading, error, features, isCoachEnterpriseEnabled, isCoachDegreesEnabled, isCoachTutorGA]);

  return { showEmbeddedCoach };
}

const CoachPermissionsWrapper = ({ children, epicImpression }: Props) => {
  const { showEmbeddedCoach } = useCoachPermissions({ epicImpression });

  return children({ showEmbeddedCoach });
};

export { CoachPermissionsWrapper, useCoachPermissions };
